import React, { useState, useEffect } from 'react';
import { View, Text, StyleSheet, TouchableOpacity, KeyboardAvoidingView, Platform, ScrollView } from 'react-native';
import { openExternal } from '../src/utils/openExternal';
import { useRouter, useLocalSearchParams } from 'expo-router';
import { Ionicons } from '@expo/vector-icons';
import { useAuth } from '../src/context/AuthContext';
import { useTheme } from '../src/context/ThemeContext';
import type { ColorTheme } from '../src/utils/theme';
import Button from '../src/components/Button';
import Input from '../src/components/Input';
import ContentContainer from '../src/components/ContentContainer';
import { spacing, fontSize, fontWeight, borderRadius } from '../src/utils/theme';
import { config } from '../src/config';
import { SMS_UI_ENABLED } from '../src/config/features';

/** RegisterScreen - Unified registration for Customer and Business Owner account types. */
export default function RegisterScreen() {
  const params = useLocalSearchParams<{ fleetInvite?: string; returnTo?: string; ref?: string }>();
  const returnTo = params.returnTo || '';
  // Owner-referral attribution: sales.rinsepay.app/<code> links land here with
  // ?ref=<code>. Prefill the (editable) referral field and default to the
  // Business Owner tab — those links only target owner acquisition.
  const urlReferralCode = params.ref
    ?? (typeof window !== 'undefined' && window.location
      ? new URLSearchParams(window.location.search).get('ref') ?? undefined
      : undefined);
  // Signup attribution: arriving via a scanned promo/gift QR (returnTo=/redeem/CODE)
  // stamps the voucher code onto the new account so owners can see which QR
  // brought each customer in.
  const signupVoucherCode = /^\/redeem\/([A-Za-z0-9]{4,32})/.exec(returnTo)?.[1];
  const fleetInviteToken = params.fleetInvite
    ?? (typeof window !== 'undefined' && window.location
      ? new URLSearchParams(window.location.search).get('fleetInvite') ?? undefined
      : undefined);
  const [accountType, setAccountType] = useState<'user' | 'owner'>(urlReferralCode && !params.fleetInvite ? 'owner' : 'user');
  const [referralCode, setReferralCode] = useState(urlReferralCode || '');
  const [email, setEmail] = useState('');
  const [emailLocked, setEmailLocked] = useState(false);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [name, setName] = useState('');
  const [businessName, setBusinessName] = useState('');
  const [phone, setPhone] = useState('');
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [smsOptIn, setSmsOptIn] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const { registerOwner, registerUser } = useAuth();
  const router = useRouter();
  const { colors } = useTheme();
  const styles = useStyles(colors);

  const MIN_PASSWORD_LENGTH = 8;

  // Fleet invite: prefill + lock email, force customer account type
  useEffect(() => {
    if (!fleetInviteToken) return;
    setAccountType('user');
    (async () => {
      try {
        const res = await fetch(`${config.apiUrl}/api/public/fleet-invites/${fleetInviteToken}/peek`);
        if (res.ok) {
          const data = await res.json();
          if (data?.email) {
            setEmail(data.email);
            setEmailLocked(true);
          }
        } else if (res.status === 410) {
          setError('This fleet invite is no longer valid. Ask the fleet owner to resend it.');
        }
      } catch {
        // silently ignore — user can still type the email manually
      }
    })();
  }, [fleetInviteToken]);

  /** Validate fields and POST to /api/auth/register-(user|owner). */
  const handleRegister = async () => {
    if (!email || !password) { setError('Please fill in all required fields'); return; }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) { setError('Please enter a valid email address (e.g. you@example.com)'); return; }
    if (password !== confirmPassword) { setError('Passwords do not match'); return; }
    if (password.length < MIN_PASSWORD_LENGTH) { setError(`Password must be at least ${MIN_PASSWORD_LENGTH} characters`); return; }
    if (!/[A-Z]/.test(password)) { setError('Password must include an uppercase letter'); return; }
    if (!/[a-z]/.test(password)) { setError('Password must include a lowercase letter'); return; }
    if (!/[0-9]/.test(password)) { setError('Password must include a number'); return; }
    if (!/[^A-Za-z0-9]/.test(password)) { setError('Password must include a special character'); return; }
    if (accountType === 'user' && !name) { setError('Name is required'); return; }
    if (accountType === 'owner' && !businessName) { setError('Business name is required'); return; }
    if (!termsAccepted) { setError('You must accept the Terms of Service'); return; }

    setError('');
    setLoading(true);
    try {
      let res;
      if (accountType === 'owner') { res = await registerOwner({ email, password, businessName, phone: phone || undefined, referralCode: referralCode.trim() || undefined, termsAccepted: true }); }
      else { res = await registerUser({ email, password, name, phone: phone || undefined, termsAccepted: true, fleetInviteToken, signupVoucherCode, smsMarketingOptIn: smsOptIn && !!phone }); }

      if (res.requiresVerification) {
        // Thread returnTo (e.g. the scanned bay) so verification lands back there. Owners always go to their dashboard.
        router.push({ pathname: '/verify-email', params: { email: res.email || email, returnTo: accountType === 'owner' ? '' : returnTo } });
      } else if (accountType === 'owner') {
        router.replace('/owner/dashboard');
      } else {
        router.replace((returnTo || '/user') as any);
      }
    } catch (err: any) { setError(err.message || 'Registration failed'); }
    finally { setLoading(false); }
  };

  return (
    <KeyboardAvoidingView style={styles.container} behavior={Platform.OS === 'ios' ? 'padding' : 'height'}>
      <ScrollView contentContainerStyle={styles.scroll} keyboardShouldPersistTaps="handled">
        <ContentContainer maxWidth={480}>
          <Text style={styles.title}>Create Account</Text>
          <Text style={styles.subtitle}>Get started with RinsePay</Text>

          <View style={styles.toggle}>
            <TouchableOpacity style={[styles.toggleBtn, accountType === 'user' && styles.toggleActive]} onPress={() => setAccountType('user')}>
              <Text style={[styles.toggleText, accountType === 'user' && styles.toggleTextActive]}>Customer</Text>
            </TouchableOpacity>
            <TouchableOpacity style={[styles.toggleBtn, accountType === 'owner' && styles.toggleActive]} onPress={() => setAccountType('owner')}>
              <Text style={[styles.toggleText, accountType === 'owner' && styles.toggleTextActive]}>Business Owner</Text>
            </TouchableOpacity>
          </View>

          {accountType === 'user'
            ? <Input label="Full Name" icon="person-outline" placeholder="John Doe" value={name} onChangeText={setName} />
            : <Input label="Business Name" icon="business-outline" placeholder="Clean Cars LLC" value={businessName} onChangeText={setBusinessName} />}

          <Input label="Email" icon="mail-outline" placeholder="you@example.com" value={email} onChangeText={emailLocked ? () => {} : setEmail} editable={!emailLocked} keyboardType="email-address" autoCapitalize="none" maxLength={255} />
          {fleetInviteToken ? (
            <Text style={{ fontSize: fontSize.xs, color: colors.textSecondary, marginTop: -spacing.sm, marginBottom: spacing.md }}>
              You&apos;re joining as a fleet manager. Email is locked to the invite recipient.
            </Text>
          ) : null}
          <Input label="Phone (optional)" icon="call-outline" placeholder="555-123-4567" value={phone} onChangeText={setPhone} keyboardType="phone-pad" />
          {accountType === 'owner' && (
            <Input label="Referral code (optional)" icon="pricetag-outline" placeholder="Who sent you?" value={referralCode} onChangeText={setReferralCode} autoCapitalize="none" autoCorrect={false} maxLength={32} />
          )}
          <View style={styles.pwChecklist}>
            <Text style={styles.pwLabel}>Password must include:</Text>
            {[
              { ok: password.length >= MIN_PASSWORD_LENGTH, label: `At least ${MIN_PASSWORD_LENGTH} characters` },
              { ok: /[A-Z]/.test(password), label: 'Uppercase letter' },
              { ok: /[a-z]/.test(password), label: 'Lowercase letter' },
              { ok: /[0-9]/.test(password), label: 'Number' },
              { ok: /[^A-Za-z0-9]/.test(password), label: 'Special character' },
            ].map((r) => (
              <View key={r.label} style={styles.pwCheckRow}>
                <Ionicons name={r.ok ? 'checkmark-circle' : 'close-circle'} size={18} color={r.ok ? colors.success : colors.textLight} />
                <Text style={[styles.pwCheckText, r.ok && { color: colors.success }]}>{r.label}</Text>
              </View>
            ))}
          </View>

          <Input label="Password" icon="lock-closed-outline" placeholder="8+ chars, upper, lower, number, symbol" value={password} onChangeText={setPassword} secureTextEntry autoCapitalize="none" autoCorrect={false} maxLength={128} />

          <Input label="Confirm Password" icon="lock-closed-outline" placeholder="Re-enter password" value={confirmPassword} onChangeText={setConfirmPassword} secureTextEntry autoCapitalize="none" autoCorrect={false} />

          <TouchableOpacity style={styles.termsRow} onPress={() => setTermsAccepted(!termsAccepted)} activeOpacity={0.7}>
            <View style={[styles.checkbox, termsAccepted && styles.checkboxChecked]}>
              {termsAccepted && <Text style={styles.checkmark}>✓</Text>}
            </View>
            <Text style={styles.termsText}>
              I have read and agree to the{' '}
              <Text style={styles.termsLink} onPress={() => { if (Platform.OS === 'web') { window.open('/terms', '_blank'); } else { openExternal(`${config.appUrl}/terms`); } }}>
                Terms of Service
              </Text>
            </Text>
          </TouchableOpacity>

          {SMS_UI_ENABLED && accountType === 'user' && (
            <TouchableOpacity style={styles.termsRow} onPress={() => setSmsOptIn(!smsOptIn)} activeOpacity={0.7}>
              <View style={[styles.checkbox, smsOptIn && styles.checkboxChecked]}>
                {smsOptIn && <Text style={styles.checkmark}>✓</Text>}
              </View>
              <Text style={styles.termsText}>
                Text me offers and updates from my car washes (optional). Msg & data rates may apply. Reply STOP anytime.
              </Text>
            </TouchableOpacity>
          )}

          {error ? <Text style={styles.error}>{error}</Text> : null}

          <Button title="Create Account" onPress={handleRegister} loading={loading} size="lg" style={styles.registerBtn} />

          <TouchableOpacity onPress={() => router.push({ pathname: '/login', params: { returnTo } })} style={styles.linkContainer}>
            <Text style={styles.linkText}>Already have an account? <Text style={styles.link}>Sign in</Text></Text>
          </TouchableOpacity>
        </ContentContainer>
      </ScrollView>
    </KeyboardAvoidingView>
  );
}

const createStyles = (c: ColorTheme) => StyleSheet.create({
  container: { flex: 1, backgroundColor: c.background },
  scroll: { flexGrow: 1, padding: spacing.lg, paddingTop: spacing.xl, paddingBottom: 120 },
  title: { fontSize: fontSize.xxl, fontWeight: fontWeight.bold, color: c.text, marginBottom: spacing.xs },
  subtitle: { fontSize: fontSize.md, color: c.textSecondary, marginBottom: spacing.lg },
  toggle: { flexDirection: 'row', backgroundColor: c.surfaceSecondary, borderRadius: borderRadius.md, padding: spacing.xs, marginBottom: spacing.lg },
  toggleBtn: { flex: 1, paddingVertical: spacing.sm + 2, alignItems: 'center', borderRadius: borderRadius.sm },
  toggleActive: { backgroundColor: c.primary, shadowColor: c.black, shadowOffset: { width: 0, height: 1 }, shadowOpacity: 0.15, shadowRadius: 3, elevation: 2 },
  toggleText: { fontSize: fontSize.sm, fontWeight: fontWeight.medium, color: c.textSecondary },
  toggleTextActive: { color: c.white },
  error: { fontSize: fontSize.sm, color: c.danger, backgroundColor: c.danger + '10', padding: spacing.md, borderRadius: borderRadius.sm, marginBottom: spacing.md, textAlign: 'center' },
  termsRow: { flexDirection: 'row', alignItems: 'center', marginTop: spacing.md, marginBottom: spacing.xs, gap: spacing.sm },
  checkbox: { width: 22, height: 22, borderRadius: 4, borderWidth: 2, borderColor: c.border, alignItems: 'center', justifyContent: 'center' },
  checkboxChecked: { backgroundColor: c.primary, borderColor: c.primary },
  checkmark: { color: c.white, fontSize: 14, fontWeight: fontWeight.bold, marginTop: -1 },
  termsText: { flex: 1, fontSize: fontSize.sm, color: c.textSecondary, lineHeight: 20 },
  termsLink: { color: c.primary, fontWeight: fontWeight.semibold },
  pwChecklist: { marginBottom: spacing.sm, gap: 3 },
  pwLabel: { fontSize: fontSize.sm, fontWeight: fontWeight.medium, color: c.textSecondary, marginBottom: 2 },
  pwCheckRow: { flexDirection: 'row', alignItems: 'center', gap: 6 },
  pwCheckText: { fontSize: fontSize.sm, color: c.textLight },
  registerBtn: { marginTop: spacing.sm },
  linkContainer: { alignItems: 'center', marginTop: spacing.lg, marginBottom: spacing.xxl },
  linkText: { fontSize: fontSize.sm, color: c.textSecondary },
  link: { color: c.primary, fontWeight: fontWeight.semibold },
});
const useStyles = (c: ColorTheme) => React.useMemo(() => createStyles(c), [c]);
